import { z } from 'zod';

import type { Product } from '@/shared/api/generated/api.generated';

export interface CartItem {
  product: Product;
  amount: number;
}

export const CART_STORAGE_KEY = 'domstankov:cart:v1';

export const getQuantityLimit = (product: Pick<Product, 'stock'>) =>
  Number.isFinite(product.stock) ? Math.max(1, Math.floor(product.stock)) : 1;

const readPrice = (value: unknown) => {
  if (typeof value !== 'number' && (typeof value !== 'string' || value.trim() === '')) return undefined;
  const price = Number(value);
  return Number.isFinite(price) && price >= 0 ? price : undefined;
};

export const getCartUnitPrice = (product: Product) =>
  readPrice(product.price.rubleWithDiscount) ?? readPrice(product.price.ruble);

// PostgreSQL decimal prices arrive as strings; optional media and prices may be null.
const storedPriceSchema = z
  .union([z.number(), z.string()])
  .refine((value) => readPrice(value) !== undefined)
  .nullish();

// Validate the fields used by the cart before accepting data from browser storage.
// Keep the complete public product snapshot for links, favourites and comparison.
const storedProductSchema = z
  .object({
    id: z.string().min(1),
    name: z.string(),
    stock: z.number().finite(),
    category: z.union([z.string().min(1), z.object({ id: z.string().min(1) }).passthrough()]),
    media: z.object({ images: z.array(z.string()), video: z.string().nullish() }).passthrough(),
    price: z
      .object({
        id: z.string(),
        ruble: storedPriceSchema,
        rubleWithDiscount: storedPriceSchema,
      })
      .passthrough(),
    attributes: z.record(z.unknown()),
  })
  .passthrough();

const storedCartSchema = z
  .array(z.object({ product: storedProductSchema, amount: z.number().int().positive() }))
  .refine((items) => new Set(items.map(({ product }) => product.id)).size === items.length)
  .refine((items) => items.every(({ product, amount }) => amount <= getQuantityLimit(product)));

export const isStoredCart = (value: unknown): value is CartItem[] => storedCartSchema.safeParse(value).success;
